// src/components/PackageCard.tsx
import React from 'react';
import { useNavigate } from 'react-router-dom';

import type { StaticPackage } from '../data/packagesData';

type Props = {
  pkg: StaticPackage;
};

const PackageCard: React.FC<Props> = ({ pkg }) => {
  const navigate = useNavigate();

  return (
    <article className="flex flex-col rounded-2xl overflow-hidden border border-white/10 bg-white/5 shadow-lg">
      <div className="relative h-44 md:h-56 lg:h-48 w-full">
        <img
          src={pkg.image}
          alt={pkg.title}
          className="w-full h-full object-cover"
        />
        {pkg.availability && (
          <div className="absolute top-3 left-3 bg-amber-600/90 text-white text-xs px-3 py-1 rounded-full shadow">{pkg.availability}</div>
        )}
      </div>

      <div className="p-5 flex flex-col gap-3">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg md:text-xl font-bold text-white">{pkg.title}</h3>
            {pkg.subtitle && <div className="text-sm text-amber-100">{pkg.subtitle}</div>}
          </div>
        </div>

        <p className="text-sm text-cyan-100 leading-relaxed">{pkg.description}</p>

        {pkg.includes && pkg.includes.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-white mt-2">Includes</h4>
            <ul className="text-sm text-cyan-100 list-disc list-inside space-y-1 mt-1">
              {pkg.includes.map((inc) => (
                <li key={inc}>{inc}</li>
              ))}
            </ul>
          </div>
        )}

        {pkg.notes && pkg.notes.length > 0 && (
          <div className="text-sm text-amber-100 bg-white/5 px-3 py-2 rounded-lg border border-white/10">
            {pkg.notes.map((n) => (
              <p key={n}>{n}</p>
            ))}
          </div>
        )}

        {pkg.pricing && pkg.pricing.length > 0 && (
          <div className="mt-2">
            <h4 className="text-sm font-semibold text-white">Pricing</h4>
            <div className="flex flex-wrap gap-2 mt-2">
              {pkg.pricing.map((p) => (
                <div key={p.group} className="text-sm text-cyan-200 bg-white/5 px-3 py-2 rounded-lg border border-white/10">
                  <div className="font-medium text-white">{p.price}</div>
                  <div className="text-xs text-cyan-100">{p.group}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {pkg.extras && pkg.extras.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-white mt-2">Extras</h4>
            <ul className="text-sm text-cyan-100 list-inside mt-1">
              {pkg.extras.map((e) => (
                <li key={e.label}>{e.label} {e.price && <span className="text-amber-200">— {e.price}</span>}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-cyan-200">{pkg.minimum ? `Minimum: ${pkg.minimum}` : ''}</div>
          <div className="flex items-center gap-3">
            {pkg.bookingAvailable ? (
              <button
                  type="button"
                  onClick={() => navigate(`/contact?package=${encodeURIComponent(pkg.title)}`)}
                  className="btn-primary px-4 py-2 rounded-xl"
                >
                Book Now
              </button>
            ) : (
                <button type="button" className="px-4 py-2 rounded-xl bg-gray-600 text-white/80" disabled>
                Not Available
              </button>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

export default PackageCard;
